import { useToast } from '@entur/alert';
import { PrimaryButton, SecondaryButton } from '@entur/button';
import { Loader } from '@entur/loader';
import ErrorBoundary from 'app/components/ErrorBoundary';
import ButtonRow from 'components/ButtonRow';
import ErrorMessageNew from 'components/ErrorMessageNew';
import PageHeader from 'components/PageHeader';
import { useCreateTransactionType } from 'features/transaction-types/api/createTransactionType';
import { TransactionTypeForm } from 'features/transaction-types/components/TransactionTypesForm';
import { Suspense } from 'react';
import { Head } from 'components/Head';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { TransactionTypeDto } from 'types';

const TransactionTypeCreateRoute = () => {
  const intl = useIntl();

  return (
    <>
      <Head title={intl.formatMessage({ id: 'transactionTypes.name' })} />
      <PageHeader
        heading={intl.formatMessage({ id: 'transactionTypes.name' })}
        element={intl.formatMessage({ id: 'generalValues.createNew' })}
        notId
      />
      <ErrorBoundary>
        <Suspense fallback={<Loader>{intl.formatMessage({ id: 'generalValues.loading' })}</Loader>}>
          <TransactionTypeCreate />
        </Suspense>
      </ErrorBoundary>
    </>
  );
};

const TransactionTypeCreate = () => {
  const intl = useIntl();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const form = useForm<TransactionTypeDto>({
    mode: 'onBlur',
    defaultValues: { name: '', type: '', status: 1 },
  });

  const { handleSubmit } = form;

  const { isPending, mutate, error } = useCreateTransactionType({
    mutationConfig: {
      onSuccess: (response) => {
        addToast(intl.formatMessage({ id: 'toastAlerts.added' }));
        if (response?.data.id) {
          navigate(`/transaction-types/${response.data.id}`, { replace: true });
        }
      },
    },
  });

  const handleCreate = (data: TransactionTypeDto) => {
    mutate({ data });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <>
      <ErrorMessageNew error={error} expandable={false} />
      <TransactionTypeForm formContext={form} mode={'create'} />
      <ButtonRow>
        <ButtonRow.RightGroup>
          <>
            <SecondaryButton onClick={handleCancel}>
              {intl.formatMessage({ id: 'generalValues.cancel' })}
            </SecondaryButton>
            <PrimaryButton onClick={handleSubmit(handleCreate)} loading={isPending} disabled={isPending}>
              {intl.formatMessage({ id: 'generalValues.create' })}
            </PrimaryButton>
          </>
        </ButtonRow.RightGroup>
      </ButtonRow>
    </>
  );
};

export default TransactionTypeCreateRoute;
